/**
 * Getting Started - Photo Upload Component
 * Alpine.js component for multi-photo upload with immediate upload on selection
 * Uses shared photo-uploader utility for uploads
 */

import { uploadPhoto, deletePhoto, parseUploadError, createEmptySlot } from '../photo-uploader.js';
import { editorBase } from '../alpine-editor-base.js';

const MAX_PHOTOS = 6;
const ALBUM_ENDPOINT = '/v1/album';

/**
 * Photo upload component factory
 * Handles multiple file selection, previews, drag/drop with immediate uploads
 * @param {Array} existingPhotos - Array of existing photos with {id, url} properties
 * @param {number} albumId - The album ID for the profile photos
 */
export function gettingStartedPhoto(existingPhotos = [], albumId = null) {
    const initialPhotos = Array.from({ length: MAX_PHOTOS }, (_, index) => {
        const existing = existingPhotos[index];
        if (existing) {
            return {
                file: null,
                preview: existing.url,
                uploading: false,
                uploaded: true,
                progress: 100,
                error: null,
                photoId: existing.id
            };
        }
        return createEmptySlot();
    });

    return {
        photos: initialPhotos,
        albumId: albumId,
        error: null,
        globalDragOver: false,
        dragOverSlot: null,
        activeSlotIndex: null,
        deleteConfirmSlot: null,
        replaceConfirmSlot: null,
        pendingReplaceFile: null,
        uploadControllers: new Map(),

        get uploadedCount() {
            return this.photos.filter(p => p.uploaded).length;
        },

        get isAnyUploading() {
            return this.photos.some(p => p.uploading);
        },

        get canSubmit() {
            return this.photos[0].uploaded && !this.isAnyUploading;
        },

        handleSlotClick(slotIndex) {
            if (this.photos[slotIndex].uploading) return;
            this.activeSlotIndex = slotIndex;
            this.$refs.fileInput.click();
        },

        handleFileSelect(event) {
            const files = Array.from(event.target.files || []);
            if (!files.length) return;

            this.error = null;
            const file = files[0];

            if (this.activeSlotIndex !== null) {
                const slot = this.photos[this.activeSlotIndex];
                // If slot has an existing uploaded photo, confirm replacement
                if (slot.uploaded && slot.photoId) {
                    this.pendingReplaceFile = file;
                    this.replaceConfirmSlot = this.activeSlotIndex;
                    this.activeSlotIndex = null;
                    event.target.value = '';
                    return;
                }
                this.addFileToSlot(file, this.activeSlotIndex);
            } else {
                this.addFilesToEmptySlots(files);
            }

            this.activeSlotIndex = null;
            event.target.value = '';
        },

        cancelReplace() {
            this.replaceConfirmSlot = null;
            this.pendingReplaceFile = null;
        },

        async executeReplace() {
            const slotIndex = this.replaceConfirmSlot;
            const file = this.pendingReplaceFile;

            if (slotIndex === null || !file) return;

            const oldPhotoId = this.photos[slotIndex].photoId;
            this.replaceConfirmSlot = null;
            this.pendingReplaceFile = null;

            // Store old photo ID to delete after successful upload
            this.photos[slotIndex].oldPhotoIdToDelete = oldPhotoId;

            // Upload new photo (will replace in slot)
            this.addFileToSlot(file, slotIndex);
        },

        addFileToSlot(file, slotIndex) {
            if (!file || !file.type.startsWith('image/')) return;

            // Preserve old photo ID if this is a replacement
            const oldPhotoIdToDelete = this.photos[slotIndex].oldPhotoIdToDelete || null;

            // Revoke blob URLs to prevent memory leaks (but not server URLs from existing photos)
            const currentPreview = this.photos[slotIndex].preview;
            if (currentPreview && currentPreview.startsWith('blob:')) {
                URL.revokeObjectURL(currentPreview);
            }

            this.photos[slotIndex] = {
                file,
                preview: URL.createObjectURL(file),
                uploading: true,
                uploaded: false,
                progress: 0,
                error: null,
                photoId: null,
                oldPhotoIdToDelete
            };

            this.doUpload(file, slotIndex);
        },

        addFilesToEmptySlots(files) {
            let fileIndex = 0;
            for (let i = 0; i < this.photos.length && fileIndex < files.length; i++) {
                if (!this.photos[i].preview && !this.photos[i].uploading) {
                    const file = files[fileIndex];
                    if (file && file.type.startsWith('image/')) {
                        this.addFileToSlot(file, i);
                        fileIndex++;
                    }
                }
            }
        },

        async doUpload(file, slotIndex) {
            // Create abort controller for this upload
            const controller = new AbortController();
            this.uploadControllers.set(slotIndex, controller);

            try {
                const result = await uploadPhoto({
                    file,
                    albumId: this.albumId,
                    controller,
                    onProgress: (percent) => {
                        this.photos[slotIndex].progress = percent;
                    }
                });

                this.photos[slotIndex].uploading = false;
                this.photos[slotIndex].uploaded = true;
                this.photos[slotIndex].progress = 100;
                this.photos[slotIndex].photoId = String(result.id);

                // Delete old photo after successful upload (for replacements)
                const oldPhotoId = this.photos[slotIndex].oldPhotoIdToDelete;
                if (oldPhotoId) {
                    this.photos[slotIndex].oldPhotoIdToDelete = null;
                    try {
                        await deletePhoto(oldPhotoId);
                    } catch {
                        // Ignore delete errors - old photo may already be gone
                    }
                }
            } catch (err) {
                this.photos[slotIndex].uploading = false;
                this.photos[slotIndex].oldPhotoIdToDelete = null;

                // Don't show error for cancelled uploads
                const errorMsg = parseUploadError(err);
                if (errorMsg !== 'Upload cancelled') {
                    this.photos[slotIndex].error = errorMsg;
                }
            } finally {
                this.uploadControllers.delete(slotIndex);
            }
        },

        confirmDeletePhoto(slotIndex) {
            if (this.photos[slotIndex].uploading) return;

            if (this.uploadedCount === 1 && this.photos[slotIndex].uploaded) {
                this.showError('You must have at least one profile photo.');
                return;
            }

            if (this.photos[slotIndex].photoId) {
                this.deleteConfirmSlot = slotIndex;
                this.refreshIcons();
            } else {
                this.removePhotoLocally(slotIndex);
            }
        },

        cancelDelete() {
            this.deleteConfirmSlot = null;
        },

        async executeDelete() {
            const slotIndex = this.deleteConfirmSlot;
            if (slotIndex === null) return;

            const photoId = this.photos[slotIndex].photoId;
            this.deleteConfirmSlot = null;

            if (photoId) {
                try {
                    await deletePhoto(photoId);
                    this.removePhotoLocally(slotIndex);
                } catch (err) {
                    this.showError(parseUploadError(err) || 'Failed to delete photo. Please try again.');
                }
            } else {
                this.removePhotoLocally(slotIndex);
            }
        },

        removePhotoLocally(slotIndex) {
            // Abort any in-progress upload for this slot
            const controller = this.uploadControllers.get(slotIndex);
            if (controller) {
                controller.abort();
                this.uploadControllers.delete(slotIndex);
            }

            // Revoke blob URLs to prevent memory leaks (but not server URLs)
            const currentPreview = this.photos[slotIndex].preview;
            if (currentPreview && currentPreview.startsWith('blob:')) {
                URL.revokeObjectURL(currentPreview);
            }

            // Shift remaining photos to fill the gap (also shift upload controllers)
            for (let i = slotIndex; i < this.photos.length - 1; i++) {
                this.photos[i] = this.photos[i + 1];
                // Shift upload controllers to maintain correct slot mapping
                if (this.uploadControllers.has(i + 1)) {
                    this.uploadControllers.set(i, this.uploadControllers.get(i + 1));
                    this.uploadControllers.delete(i + 1);
                }
            }
            // Add empty slot at the end
            this.photos[this.photos.length - 1] = createEmptySlot();
        },

        refreshIcons() {
            this.$nextTick(() => {
                if (window.lucide) {
                    window.lucide.createIcons();
                }
            });
        },

        async setAsPrimary(slotIndex) {
            if (this.photos[slotIndex].uploading || this.photos[0].uploading) return;

            const photoId = this.photos[slotIndex].photoId;
            if (!photoId || !this.albumId) return;

            const token = editorBase.getAuthToken();
            if (!token) {
                this.showError('Authentication required');
                return;
            }

            const clickedPhoto = { ...this.photos[slotIndex] };
            const primaryPhoto = { ...this.photos[0] };
            this.photos[0] = clickedPhoto;
            this.photos[slotIndex] = primaryPhoto;

            try {
                const formData = new FormData();
                formData.append('album_cover_id', photoId);

                const response = await fetch(`${ALBUM_ENDPOINT}/${this.albumId}`, {
                    method: 'PUT',
                    headers: {
                        'X-AUTH-TOKEN': token
                    },
                    body: formData,
                    credentials: 'include'
                });

                if (!response.ok) {
                    this.photos[slotIndex] = clickedPhoto;
                    this.photos[0] = primaryPhoto;
                    const errorMessage = await editorBase.handleApiError(response, 'album_cover_id');
                    this.showError(errorMessage || 'Failed to set as profile photo. Please try again.');
                }
            } catch {
                this.photos[slotIndex] = clickedPhoto;
                this.photos[0] = primaryPhoto;
                this.showError('Failed to set as profile photo. Please try again.');
            }
        },

        handleSlotDrop(event, slotIndex) {
            this.dragOverSlot = null;
            this.globalDragOver = false;

            if (this.photos[slotIndex].uploading) return;

            const files = event.dataTransfer?.files;
            if (files?.length) {
                this.addFileToSlot(files[0], slotIndex);
            }
        },

        handleGlobalDrop(event) {
            this.globalDragOver = false;
            this.dragOverSlot = null;

            const files = Array.from(event.dataTransfer?.files || []);
            if (files.length) {
                this.addFilesToEmptySlots(files);
            }
        },

        showError(message) {
            this.error = message;
        },

        hideError() {
            this.error = null;
        },

        handleSubmit() {
            if (!this.canSubmit) {
                if (this.isAnyUploading) {
                    this.showError('Please wait for uploads to complete.');
                } else {
                    this.showError('Please upload at least one photo.');
                }
                return;
            }

            window.location.href = '/app/getting-started/done';
        }
    };
}
